import React from 'react';
import { ISigningAppointmentDocumentResponse } from '../../../../../../../types/signingAppointment/ISigningAppointmentDocumentResponse';
import { UUID } from '../../../../../../../types/shared/UUID';
import { Show } from '../../../../../../shared/Show';
import { TrashIcon } from '@heroicons/react/24/outline';
import { OutgoingTooltip } from './OutgoingTooltip';
import { BadgeTW } from '../../../../../../../tailwindComponents/badgeTW/BadgeTW';
import { isSent } from '../Utils';
import { useUpdateSigningAppointmentDocumentMutation } from '../../../../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { checkForMutationSuccess } from '../../../../../../../API/checkForMutationSuccess';
import { useParams } from 'react-router-dom';

const pinkBadgeClassName = 'whitespace-nowrap bg-pink-100 text-pink-800';
const greenBadgeClassName = 'whitespace-nowrap bg-green-100 text-green-800';
const useDetermineButtonClassNameAndText = (document: ISigningAppointmentDocumentResponse) => {
  if (document.markedForDeletionAt) {
    return { className: pinkBadgeClassName, text: 'Marked For Deletion' };
  }
  if (!isSent(document)) {
    return { className: pinkBadgeClassName, text: 'Not Sent' };
  }
  return { className: greenBadgeClassName, text: 'Sent to SigSync' };
};

export const TrashAndBadges: React.FC<{
  isSignatureSyncAppointment?: boolean;
  document: ISigningAppointmentDocumentResponse;
}> = ({ isSignatureSyncAppointment, document }) => {
  const { appointmentId, orderId } = useParams<{ appointmentId: UUID; orderId: UUID }>();
  const { className, text } = useDetermineButtonClassNameAndText(document);
  const [updateSigningAppointmentDocument] = useUpdateSigningAppointmentDocumentMutation();

  const onDeleteAssociatedDocument = async (id: UUID) => {
    const res = await updateSigningAppointmentDocument({
      orderId,
      signingAppointmentId: appointmentId,
      standardDocumentId: id,
      markForDeletion: true,
    });
    checkForMutationSuccess(res, '', undefined, {
      success: {
        title: 'Document Successfully Marked For Deletion',
        description: 'Document will be removed next time documents are sent to Sig Sync',
      },
      error: {
        title: 'Marked For Deletion Failed',
        description: 'Document was not marked for deletion, please try again',
      },
    });
  };

  if (!isSignatureSyncAppointment) return null;
  return (
    <div className={'flex flex-row items-center gap-2'} data-test-id={`${document.id}-trash-and-badges`}>
      <Show when={!document.markedForDeletionAt}>
        <button
          onClick={(event) => {
            event.stopPropagation();
            void onDeleteAssociatedDocument(document.id);
          }}
        >
          <TrashIcon className="h-4 w-4 cursor-pointer text-red-600" data-test-id="delete-sad-button" />
        </button>
      </Show>
      <OutgoingTooltip document={document}>
        <BadgeTW className={className}>{text}</BadgeTW>
      </OutgoingTooltip>
    </div>
  );
};
